import { css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import {
	useAddUserToLevAdminGroupMutation,
	useGetAllLevAdminGroups,
	useGetUserLevAdminGroups,
	useRemoveUserFromLevAdminGroupMutation,
} from '../../../../queries';
import { Button } from '../../../../web/components-v2/Button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../web/components-v2/Select';
import { Modal } from '../../../../web/components/Modal';
import { TrashIcon } from '../../../../web/components/svgs/icons/TrashIcon';
import { destructive } from '../../../../web/styles/colors';
import { styleSheet } from './styles';

const getFriendlyRoleName = (role: string): string => {
	return role.replace('Levitate:', '');
};

const getFullRoleName = (role: string): string => {
	// Ensure role has "Levitate:" prefix
	return role.startsWith('Levitate:') ? role : `Levitate:${role}`;
};

export const ManageInternalRolesModal = ({
	userId,
	userName,
	onClose,
}: {
	userId: string;
	userName: string;
	onClose: () => void;
}) => {
	const [selectedRole, setSelectedRole] = React.useState<string>('');

	// Fetch all available roles and user's current roles
	const { data: allRoles, isLoading: isLoadingAllRoles } = useGetAllLevAdminGroups();
	const {
		data: userRoles,
		isLoading: isLoadingUserRoles,
		refetch: refetchUserRoles,
	} = useGetUserLevAdminGroups(userId);

	// Mutations
	const addRoleMutation = useAddUserToLevAdminGroupMutation();
	const removeRoleMutation = useRemoveUserFromLevAdminGroupMutation();

	const isLoading = isLoadingAllRoles || isLoadingUserRoles;
	const currentRoles = userRoles || [];
	// Normalize current roles to full names for comparison
	const currentRolesFullNames = currentRoles.map(role => getFullRoleName(role));
	// Filter out roles that are already assigned (comparing full names)
	const availableRolesToAdd = (allRoles || []).filter(role => {
		const fullRoleName = getFullRoleName(role);
		return !currentRolesFullNames.includes(fullRoleName);
	});

	const handleAddRole = async () => {
		if (!selectedRole) {
			return;
		}

		try {
			// Ensure we send full role name to API (e.g., "Levitate:Sales")
			const fullRoleName = getFullRoleName(selectedRole);
			await addRoleMutation.mutateAsync({ userId, groupName: fullRoleName });
			toast.success(`Role "${getFriendlyRoleName(selectedRole)}" added successfully`);
			setSelectedRole('');
			await refetchUserRoles();
		} catch (error: unknown) {
			const errorMessage =
				error && typeof error === 'object' && 'systemMessage' in error ? String(error.systemMessage) : 'Unknown error';
			toast.error(`Failed to add role: ${errorMessage}`);
		}
	};

	const handleRemoveRole = async (roleName: string) => {
		try {
			// Ensure we send full role name to API (e.g., "Levitate:Sales")
			const fullRoleName = getFullRoleName(roleName);
			await removeRoleMutation.mutateAsync({ userId, groupName: fullRoleName });
			toast.success(`Role "${getFriendlyRoleName(roleName)}" removed successfully`);
			await refetchUserRoles();
		} catch (error: unknown) {
			const errorMessage =
				error && typeof error === 'object' && 'systemMessage' in error ? String(error.systemMessage) : 'Unknown error';
			toast.error(`Failed to remove role: ${errorMessage}`);
		}
	};

	const isProcessing = addRoleMutation.isPending || removeRoleMutation.isPending;

	return (
		<Modal isOpen>
			<article className={css(styleSheet.modalContainer)}>
				<div>
					<h3>Manage Internal Roles for {userName}</h3>
				</div>

				{isLoading ? (
					<div className={css(styleSheet.loadingContainer)}>
						<p>Loading roles...</p>
					</div>
				) : (
					<>
						{/* Current Roles Section */}
						<div className={css(styleSheet.section)}>
							<h4>Current Roles</h4>
							{currentRoles.length === 0 ? (
								<p className={css(styleSheet.emptyMessage)}>No roles assigned</p>
							) : (
								<ul className={css(styleSheet.rolesList)}>
									{currentRoles.map(role => (
										<li key={role} className={css(styleSheet.roleItem)}>
											<span className={css(styleSheet.roleName)}>{getFriendlyRoleName(role)}</span>
											<button
												onClick={() => handleRemoveRole(role)}
												disabled={isProcessing}
												className={css(styleSheet.removeButton)}
												title={`Remove ${getFriendlyRoleName(role)}`}
											>
												<TrashIcon fillColor={destructive} />
											</button>
										</li>
									))}
								</ul>
							)}
						</div>

						{/* Add Role Section */}
						<div className={css(styleSheet.section)}>
							<h4>Add Role</h4>
							{availableRolesToAdd.length === 0 ? (
								<p className={css(styleSheet.emptyMessage)}>All roles assigned</p>
							) : (
								<div className={css(styleSheet.addRoleContainer)}>
									<Select value={selectedRole} onValueChange={setSelectedRole} disabled={isProcessing}>
										<SelectTrigger styles={styleSheet.roleSelect}>
											<SelectValue placeholder='Select a role...' />
										</SelectTrigger>
										<SelectContent>
											{availableRolesToAdd.map(role => (
												<SelectItem key={role} value={role}>
													{getFriendlyRoleName(role)}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
									<Button
										onClick={handleAddRole}
										disabled={!selectedRole || isProcessing}
										isLoading={isProcessing}
										styles={[styleSheet.addButton]}
									>
										Add
									</Button>
								</div>
							)}
						</div>
					</>
				)}

				{/* Footer */}
				<div className={css(styleSheet.modalActions)}>
					<Button onClick={onClose} disabled={isProcessing} variant='secondary'>
						Close
					</Button>
				</div>
			</article>
		</Modal>
	);
};
